import { useMemo } from 'react';
import { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import type {
  ChecklistItem,
  ChecklistView,
  Env,
  ReleaseBuild,
} from '@internal/backstage-plugin-bidone-backend';
import { useReleaseBuilds } from './useReleaseBuilds';
import { useReleaseChecklist } from './useReleaseChecklist';
import { findComponentByRepo, useFetchEntities } from './useCatalog';
import { repoNameFromUrl, useLatestRepoTags } from './useDeploymentGroups';
import { useReleaseDeployments } from './useReleaseDeployments';
import { useConfiguredRegions } from './useCommon';
import { pickBestByFuzzyMatch } from './fuzzyNameMatch';
import { useBitbucketTags } from './useBitbucket';

// Repo rows that opt out of the default Azure-driven region rendering and
// fall back to template-declared editable checkboxes (the same path
// non-repo rows take).
//
// Two flavours, distinguished by `suppressAzure`:
//   - `suppressAzure: true` → always manual; ignore any Azure data the
//     lookup returns. Used when the row shares a repo with a sibling that
//     owns the Azure labels (the ticks would otherwise be the sibling's
//     state, not this row's).
//   - default (`suppressAzure` unset) → manual only when Azure has no
//     data for this repo. Used for components that *can* be Azure-tracked
//     in principle but currently aren't (MFE static-content tagging
//     not wired locally, data factories without `GitTagOrBranch`, etc.).
//     If Azure data does turn up, the row goes back to being Azure-driven
//     automatically.
//
// `textPattern` disambiguates rows that share a repo; omit when the repo
// only has one checklist row in the template.
const MANUAL_TICK_ROWS: Array<{
  repo: string;
  textPattern?: RegExp;
  suppressAzure?: boolean;
}> = [
  // Database rows share their Bitbucket repo with the corresponding API
  // row in another section, which owns the Azure labels. The DB rows
  // therefore need manual ticks; suppressAzure stops the sibling's ticks
  // from bleeding through. The `textPattern` disambiguates the DB row
  // from any other row that happens to share the same repo.
  { repo: 'bidvestdirect', textPattern: /db/i, suppressAzure: true },
  { repo: 'opri_database', textPattern: /db/i, suppressAzure: true },
  { repo: 'bidone_audit_api', textPattern: /db/i, suppressAzure: true },
  { repo: 'menuplanning_database', textPattern: /db/i, suppressAzure: true },
  { repo: 'mds-automation', textPattern: /mongo/i, suppressAzure: true },
  // OPRI Data Factories — Azure resource tags don't surface on data
  // factories, so the user ticks deployment status manually.
  { repo: 'bdirect-datafactory', textPattern: /data\s*factory/i },
];

const overrideFor = (repo: string, text: string) =>
  MANUAL_TICK_ROWS.find(
    o => o.repo === repo && (!o.textPattern || o.textPattern.test(text)),
  );

// Resolve the target environment for a release name. Mirrors the convention
// used by `useReleaseDeployments`'s `envFromRelease`:
//   *-rc<n>             → UAT
//   *-uat               → UAT
//   everything else     → PROD  (covers *-prod and out-of-band names)
// Restricted to the `Env` union the template uses for region checkboxes,
// so `preprod` / `qa` / `dev` collapse into PROD here — those releases don't
// declare their own checkbox sets in the BDirect template.
const releaseEnv = (release: string): Env => {
  const lower = release.toLowerCase();
  if (/-rc\d*$/.test(lower)) return 'UAT';
  if (lower.endsWith('-uat')) return 'UAT';
  return 'PROD';
};

// Enriched checklist row used by the BDirect table. Steps with a `repo` pick
// up their matching ADO build + catalog component + per-region deployment
// tags; sections and repo-less steps carry the bare ChecklistItem fields
// plus empty defaults so consumers don't need to null-check the join data.
export type BDirectRow = ChecklistItem & {
  build?: ReleaseBuild;
  component?: Entity;
  owner?: string;
  // Region (lowercase) → currently-deployed tag in the release's target env.
  // Empty when no repo, or no deployment data for that repo yet.
  perRegionTag: Record<string, string>;
  // When true, render region cells as template-declared (editable) instead
  // of Azure-driven, even though the row has a repo. Used for repo rows
  // listed in MANUAL_TICK_ROWS — see the comment on that constant.
  manualTicks: boolean;
  // Most recent Bitbucket tag for this row's repo by timestamp, irrespective
  // of the `-prod` / `-rc` suffix. Empty for non-repo rows or while the
  // tag query is in flight.
  latestTag?: string;
  // Region (lowercase) → currently-deployed tag in the **preprod** env,
  // regardless of the release's target env. Feeds the `AUE-PREPROD` /
  // `WEU-PREPROD` columns. Empty when no repo, or no preprod deployment
  // data for that repo yet.
  perRegionPreprodTag: Record<string, string>;
};

export interface BDirectRowsResult {
  release: string | null;
  env: Env;
  view: ChecklistView | undefined;
  items: BDirectRow[];
  regions: string[];
  isFetching: boolean;
  // True while any of the downstream join queries (builds, deployments,
  // preprod deployments, bitbucket tags) are still in flight. The
  // `view`/`items` may already be populated by then — this lets callers
  // (notably the print page) hold off rendering until every column's
  // data has settled.
  isHydrating: boolean;
  error: Error | null;
}

// Joins the checklist (template-driven, ordered list of sections + steps)
// against the live ADO build feed, catalog components, and Azure deployment
// tags. Returns one enriched row per checklist item, in checklist order.
//
// Build / deployment queries are gated on "any row currently Tagged" —
// pre-tagging release weeks skip both Azure calls entirely; the first
// tag flip enables the queries and every row picks up data from the
// shared cache.
export const useBDirectRows = (
  release: string | null,
): BDirectRowsResult => {
  const catalogApi = useApi(catalogApiRef);
  const checklist = useReleaseChecklist(release);
  const hasAnyTagged = (checklist.data?.items ?? []).some(
    i => i.state === 'tagged',
  );
  // Pass null when no rows are tagged → the hooks' `enabled: !!release`
  // gate short-circuits the fetch. As soon as a row flips to Tagged,
  // the queryKey re-keys and TanStack Query fires both calls once.
  const buildsRelease = hasAnyTagged ? release : null;
  const { data: builds, isFetching: buildsFetching } =
    useReleaseBuilds(buildsRelease);
  const { data: components } = useFetchEntities('Component', catalogApi);
  const { data: deployments, isFetching: deploymentsFetching } =
    useReleaseDeployments(buildsRelease);
  // Preprod deployment tags feed the AUE-PREPROD / WEU-PREPROD columns
  // independent of the release's target env — preprod is always rendered.
  // Reuses the same Azure Resource Graph query plumbing as
  // `useReleaseDeployments` so the underlying calls dedupe via React Query.
  const { data: preprodDeployments, isFetching: preprodFetching } =
    useLatestRepoTags('preprod');
  const { data: regions } = useConfiguredRegions();

  // Unique repo slugs (lowercased) across every step in the checklist —
  // feeds `useBitbucketTags` so the "Current" column can render the latest
  // tag for each repo without per-row HTTP fan-out. Empty until the
  // checklist resolves.
  const repoSlugs = useMemo(() => {
    const set = new Set<string>();
    for (const item of checklist.data?.items ?? []) {
      if (item.kind === 'step' && item.repo) set.add(item.repo.toLowerCase());
    }
    return Array.from(set);
  }, [checklist.data]);
  const { data: bitbucketTags, isFetching: bitbucketTagsFetching } =
    useBitbucketTags(repoSlugs);

  const env: Env = release ? releaseEnv(release) : 'PROD';

  const items = useMemo<BDirectRow[]>(() => {
    const view = checklist.data;
    if (!view) return [];
    // Group builds by every plausible name key. Multiple builds can share
    // a repo (e.g. several Classic Releases under `bdirect-datafactory`,
    // one per Data Factory pipeline) — per-row matching picks the best
    // fit via fuzzy name overlap below. Name forms we index under:
    //   - `repositoryName` lowercased — Bitbucket pipelines often store the
    //     workspace-qualified form ("bidone/mds-automation").
    //   - The last `/`-split segment of that name — strips the workspace.
    //   - The URL-derived key from `repoNameFromUrl` — last path component
    //     of the clone URL.
    // A build can register under multiple keys; dedup per-key with a Set
    // so the same build object doesn't appear twice in the candidate list.
    const buildsByRepo = new Map<string, ReleaseBuild[]>();
    const addKey = (key: string | undefined, build: ReleaseBuild) => {
      if (!key) return;
      const lower = key.toLowerCase();
      let bucket = buildsByRepo.get(lower);
      if (!bucket) {
        bucket = [];
        buildsByRepo.set(lower, bucket);
      }
      if (!bucket.includes(build)) bucket.push(build);
    };
    for (const b of builds ?? []) {
      addKey(b.repositoryName, b);
      addKey(b.repositoryName?.split('/').pop(), b);
      addKey(repoNameFromUrl(b.repositoryUrl), b);
    }
    return view.items.map((item): BDirectRow => {
      if (item.kind === 'section' || !item.repo) {
        return {
          ...item,
          perRegionTag: {},
          manualTicks: false,
          perRegionPreprodTag: {},
        };
      }
      // The downstream lookup keys (`deployments`, `buildsByRepo`,
      // `MANUAL_TICK_ROWS`) are all lowercase. The template's Code Repo
      // cell may carry its own casing (e.g. `IdentityServer_v4`), so
      // normalise once here.
      const repoKey = item.repo.toLowerCase();
      // Multiple builds can share a repo (e.g. Data Factory pipelines all
      // sit under `bdirect-datafactory`). Pick the one whose pipeline
      // name best overlaps with the step text via fuzzy matching.
      //
      // Preference rule: when both Classic Release runs and Build runs
      // exist for a repo, the user only cares about the Release — the
      // build is just the artifact step the release consumes. We
      // restrict the candidate pool to releases when any exist, and
      // fall back to builds for pure-YAML pipelines (no release side).
      //
      // NOTE: this matcher is intentionally separate from
      // `findComponentByRepo` — catalog components and ADO pipeline names
      // follow different naming conventions and the same hint can drift
      // in opposite directions for each target.
      const allCandidates = buildsByRepo.get(repoKey) ?? [];
      const releaseCandidates = allCandidates.filter(
        c => c.kind === 'release',
      );
      const candidates =
        releaseCandidates.length > 0
          ? releaseCandidates
          : allCandidates.filter(c => c.kind === 'build');
      const build = pickBestByFuzzyMatch(
        candidates,
        b => `${b.definition.name} ${b.buildNumber ?? ''}`,
        item.text,
      );

      // Resolve the catalog component independently (using the step text
      // hint), preferring build.repositoryUrl when available for a more
      // precise match.
      const componentForRepo =
        (build &&
          findComponentByRepo(components, build.repositoryUrl, item.text)) ??
        findComponentByRepo(components, repoKey, item.text);
      // Lowercase the owner ref. Backstage entity refs are case-insensitive
      // by spec, but a manifest that carries `spec.owner: User:Foo.Bar` will
      // still surface that exact string here — which (a) breaks EntityRefLink
      // resolution (the link falls back to displaying the raw ref instead of
      // the user's title), and (b) splits the Filters owner dropdown into
      // duplicate entries for the same person. The catalog stores names
      // lowercased, so lowercasing the ref before consumers see it both
      // resolves the link and dedupes the filter list.
      const rawOwner = (
        componentForRepo?.spec as { owner?: string } | undefined
      )?.owner;
      const owner = rawOwner?.toLowerCase();
      const azureForRepo = deployments?.[repoKey] ?? {};
      const override = overrideFor(repoKey, item.text);
      // - suppressAzure → always manual, ignore any data the lookup
      //   returned (it belongs to a sibling row).
      // - default override → manual only when Azure has nothing for the
      //   repo; if data turns up later the row reverts to Azure-driven.
      const hasAzureData = Object.keys(azureForRepo).length > 0;
      const manualTicks = !!override && (
        override.suppressAzure === true || !hasAzureData
      );
      const perRegionTag = manualTicks ? {} : azureForRepo;
      const latestTag = bitbucketTags?.[repoKey]?.latest;
      // Preprod tags don't follow the manualTicks rule — the preprod
      // columns are read-only and always Azure-sourced when data exists.
      const perRegionPreprodTag = preprodDeployments?.[repoKey] ?? {};
      return {
        ...item,
        build,
        component: componentForRepo,
        owner,
        perRegionTag,
        manualTicks,
        latestTag,
        perRegionPreprodTag,
      };
    });
  }, [checklist.data, builds, components, deployments, bitbucketTags, preprodDeployments]);

  return {
    release,
    env,
    view: checklist.data,
    items,
    regions: regions ?? [],
    isFetching: checklist.isFetching,
    isHydrating:
      buildsFetching ||
      deploymentsFetching ||
      preprodFetching ||
      bitbucketTagsFetching,
    error: checklist.error ?? null,
  };
};
